import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { AuthService } from 'app/auth/services/AuthService';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { TRetryOptions, TInternalRetryState } from 'app/types';
import {
  shouldRetry,
  isNetworkError,
  expoJitterDelay,
  parseRetryAfter,
  wait
} from '../helpers/retryHelpers';

let refreshingPromise: Promise<string | undefined> | null = null;

export const setupResponseInterceptors = (
  http: AxiosInstance,
  opts: Partial<TRetryOptions> = {}
) => {
  const options: TRetryOptions = {
    maxRetries: 3,
    respectRetryAfter: true,
    backoffBase: 200,
    backoffCap: 4000,
    ...opts,
  };

  http.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const cfg = (error.config || {}) as InternalAxiosRequestConfig & TInternalRetryState;
      const status = error.response?.status;

      if (status === 401 && !cfg.__isRetryRequest) {
        try {
          if (!refreshingPromise) {
            refreshingPromise = AuthService.refreshToken().finally(() => {
              refreshingPromise = null;
            });
          }
          const newAccess = await refreshingPromise;
          if (newAccess) {
            cfg.__isRetryRequest = true;
            cfg.headers = cfg.headers || {};
            cfg.headers.Authorization = `Bearer ${newAccess}`;
            return http.request(cfg);
          }
        } catch (error) {
          Telemetry.log('warning', 'Token refresh failed in 401 handler', { error: error as Error });
          await wait(500);
        }
        return Promise.reject(error);
      }

      if (!cfg) return Promise.reject(error);

      cfg.__retryCount = cfg.__retryCount ?? 0;
      if (cfg.__retryCount >= options.maxRetries) {
        return Promise.reject(error);
      }

      const method = (cfg.method || 'GET').toUpperCase();
      const wantRetry = shouldRetry({
        method,
        status,
        isNetworkError: isNetworkError(error),
      });

      if (!wantRetry) return Promise.reject(error);

      let delay = expoJitterDelay(cfg.__retryCount, options.backoffBase, options.backoffCap);
      if (options.respectRetryAfter) {
        const raHeader = error.response?.headers?.['retry-after'] as string | undefined;
        const ra = parseRetryAfter(raHeader);
        if (ra != null) delay = Math.max(delay, ra);
      }

      cfg.__retryCount += 1;
      await wait(delay);
      return http.request(cfg);
    }
  );
};
